import React, { useContext, useEffect, useState } from "react";
import { Button, Tab, Table, Tabs, Typography, GridLegacy as Grid } from "@mui/material";
import { addDays } from "date-fns";
import { useHistory, useLocation } from "react-router-dom";
import {
  FiChevronsLeft,
  FiChevronsRight,
  FiClock,
  FiGitBranch,
  FiSettings,
} from "react-icons/fi";
import { useTranslation } from "react-i18next";

import {
  api,
  IKmosAllocationDash,
  IKmosV2AllocationView,
  IPreferences,
  LaneAllocationSystem,
} from "../../../logic/api";
import { SessionContext } from "../../../utility/useSession";
import {
  ReleaseFlag,
  useReleaseManagement,
} from "../../../utility/feature-management";
import { hasKeyMasterPermissions } from "../../../utility/utils";
import { useUserPermissions } from "@/hooks/useUserPermissions";
import { useDate } from "../../../utility/useDate";
import { usePreferences } from "../../../utility/usePreferences";

import DateField from "../../../components/editable/EditableField/fields/DateField";
import AccessDeniedMessage from "../../../components/errors/AccessDeniedMessage";
import { IKmosLayout } from "./interfaces";
import styles from "./KmosView.module.css";
import KmosDateHeaderRow from "./KmosDateHeaderRow";
import KmosTimeSlotRow from "./KmosTimeSlotRow";
import KmosTimeSlotDialog from "./KmosTimeSlotDialog";
import { KmosSettingsDialog } from "./KmosDialogSettings";
import { KmosTimeSlotEditor } from "../v1.0/components/KmosTimeSlotEditor";
import LaneAllocationView from "../v1.0/components/LaneAllocationView";
import LanesByTimeSlotContent from "./LanesByTimeSlotContent";
import PairedJourneys from "../PairedJourneys/PairedJourneys";

interface TimeSlot {
  startTime: string;
  endTime: string;
  date: string;
  timeSlot: string;
  title: string;
}

const KmosView2: React.FunctionComponent<unknown> = () => {
  const { localizeDate } = useDate();
  const { t } = useTranslation();
  const { kmosDaysToDisplay } = usePreferences();
  const daysToDisplay = kmosDaysToDisplay || 7;
  const session = useContext(SessionContext);
  const carParkId = session.current.site?.home.carParkId;
  const { hasPermission } = useUserPermissions();
  let displayDays = Number(
    session.current.site?.preferences.kmos_days_to_display,
  );
  displayDays = isNaN(displayDays) ? 7 : displayDays;

  const isKeyMasterRoleReleased = useReleaseManagement(
    ReleaseFlag.releaseKeyMasterRole,
  );
  const isLaneAllocationRulesReleased = useReleaseManagement(
    ReleaseFlag.releaseLaneAllocationRules,
  );
  const isSegmentsReleased = useReleaseManagement(
    ReleaseFlag.releaseKmosSegments,
  );
  const isMoveTimeSlotManagerReleased = useReleaseManagement(
    ReleaseFlag.moveKmosTimeSlotsSettings,
  );
  const isKeyMasterTimeSlotReleased = useReleaseManagement(
    ReleaseFlag.releaseKeyMasterTimeSlot,
  );
  const isPairedJourneysReleased = useReleaseManagement(
    ReleaseFlag.releasePairedJourneys,
  );
  const isRbacLanePlanningReleased = useReleaseManagement(
    ReleaseFlag.releaseRbacLanePlanningPermissions,
  );

  const allocationViewAPI = api.hooks.kmosV2.getAllocationView();

  // Storing the paired journey state at this level so that it's persisted across tab changes
  const {
    data: pairedJourneysData,
    status: pairedJourneysStatus,
    send: getPairedJourneysData,
  } = api.hooks.kmosV2.getBookingsWithPairedJourney();

  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [selectedTsv, setSelectedTsv] = useState<IKmosV2AllocationView>();
  const [dateRange, setDateRange] = useState<Date[]>([]);
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [dashParams, setDashParams] = useState<IKmosAllocationDash>({
    date: localizeDate(startDate).toFormat("yyyy-MM-dd"),
  });
  const [showAllocationRules, setShowAllocationRules] =
    useState<boolean>(false);
  const [showTimeSlotEditor, setShowTimeSlotEditor] = useState<boolean>(false);
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [tsViews, setTsViews] = useState<IKmosV2AllocationView[]>([]);
  const [uniqueTimeSlots, setUniqueTimeSlots] = useState<TimeSlot[]>([]);
  const [activeTab, setActiveTab] = useState<number>(getTabFromUrl());
  const history = useHistory();
  const location = useLocation();

  // Update URL on tab changes
  const handleTabChange = (
    event: React.ChangeEvent<object>,
    newValue: number,
  ) => {
    const params = new URLSearchParams(location.search);
    params.set("tab", newValue.toString());
    history.push({ search: params.toString() });
    setActiveTab(newValue);
  };

  const updatePrefsApi = api.hooks.preferences.update();

  const savePrefs = async (newPrefs: IPreferences) => {
    return new Promise<boolean>((accept) => {
      const parameters = {
        kmos_days_to_display: newPrefs.kmos_days_to_display,
        kmos_duein_hours_ahead: newPrefs.kmos_duein_hours_ahead,
      }
      updatePrefsApi.send(session.current.context, parameters, (updatedPrefs) => {
        if (updatedPrefs != null) {
          session.setPreferences(updatedPrefs);
          setShowSettings(false);
          getTsViews();
          accept(true);
        } else {
          accept(false);
        }
      });
    });
  };

  useEffect(() => {
    setDashParams({
      date: localizeDate(startDate).toFormat("yyyy-MM-dd"),
    });
  }, [startDate]);

  useEffect(() => {
    getTsViews();
  }, [dashParams, carParkId]);

  useEffect(() => {
    if (!tsViews.length) return;

    setDateRange(getDatesInRange(startDate, daysToDisplay));
    getUniqueTimeSlots();
  }, [tsViews]);

  const getDatesInRange = (startDate: Date, range: number) => {
    return Array.from({ length: range }, (_, i) => i).map((x) =>
      addDays(startDate, x),
    );
  };

  const getTsViews = () => {
    allocationViewAPI.send(
      session.current.context,
      dashParams,
      (returnedTsViews) => {
        if (!returnedTsViews) return;

        setTsViews(returnedTsViews);
      },
    );
  };

  const getUniqueTimeSlots = () => {
    const timeSlots = tsViews.map((tsView) => {
      return {
        timeSlot: tsView.timeSlotDate.timeSlot,
        title: tsView.timeSlot.title,
        startTime: tsView.timeSlot.startTime,
        endTime: tsView.timeSlot.endTime,
        date: tsView.timeSlotDate.date,
      };
    });

    const uniqueTimeSlots: TimeSlot[] = [];

    timeSlots.forEach((timeSlot: TimeSlot) => {
      if (
        !uniqueTimeSlots.find(
          (uts: TimeSlot) =>
            uts.startTime === timeSlot.startTime &&
            uts.endTime === timeSlot.endTime,
        )
      ) {
        uniqueTimeSlots.push(timeSlot);
      }
    });

    setUniqueTimeSlots(uniqueTimeSlots);
  };

  const getViewsByTimeSlot = (ts: TimeSlot) => {
    return tsViews.filter(
      (tsView) =>
        tsView.timeSlot.startTime === ts.startTime &&
        tsView.timeSlot.endTime === ts.endTime,
    );
  };

  const updateTimeSlotEditor = () => {
    setShowTimeSlotEditor(false);

    allocationViewAPI.send(session.current.context, dashParams, (results) => {
      const newLayout: IKmosLayout = {
        letters: [],
        returnDates: [],
        timeslots: [],
      };
      if (results != null) {
        for (const tsView of results) {
          if (newLayout.returnDates.indexOf(tsView.timeSlotDate.date) == -1) {
            newLayout.returnDates.push(tsView.timeSlotDate.date);
          }
          if (newLayout.timeslots.indexOf(tsView.timeSlotDate.timeSlot) == -1) {
            newLayout.timeslots.push(tsView.timeSlotDate.timeSlot);
            newLayout.letters.push(tsView.timeSlotDate.timeSlot);
          }
        }
      }

      setTsViews(results as unknown as IKmosV2AllocationView[]);
    });
  };

  const shouldRenderPairedJourneysTab =
    isPairedJourneysReleased &&
    session.current.site?.preferences?.dynamic_allocation_paired_journeys &&
    session.current.site?.preferences.lane_allocation_system ===
    LaneAllocationSystem.DAS;

  const canAccessKmos = isRbacLanePlanningReleased
    ? hasPermission("lanePlanning.planning.view")
    : hasKeyMasterPermissions(session);

  const canUpdateKmosRules = isRbacLanePlanningReleased ? hasPermission("lanePlanning.rule.update") : hasKeyMasterPermissions(session);

  if (!canAccessKmos) {
    return <AccessDeniedMessage />;
  }

  return (
    <>
      {selectedTsv && (
        <KmosTimeSlotDialog
          tsView={selectedTsv}
          isOpen={isDialogOpen}
          handleClose={() => {
            setSelectedTsv(undefined);
            setIsDialogOpen(false);
            getTsViews();
          }}
        />
      )}
      <Grid
        id="kmos-header"
        container
        columns={4}
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1000,
          p: 2,
          backgroundColor: "white",
          borderBottom: `1px solid #eee`,
        }}
      >
        <Grid item xs={1}>
          <Typography
            variant="h4"
            fontWeight="bold"
            sx={{
              color: (theme) => theme.palette.brand.main,
            }}
          >
            {t("kmos.labels.keyMaster")}
          </Typography>
        </Grid>
        {activeTab !== 2 && (
          <>
            <Grid
              id="kmos-header-date-controls"
              item
              xs={2}
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Button
                variant="contained"
                onClick={() => {
                  setStartDate(
                    new Date(
                      startDate.setDate(startDate.getDate() - displayDays),
                    ),
                  );
                }}
                startIcon={<FiChevronsLeft size={16} />}
              >
                {t("kmos.labels.previousDays", { days: displayDays })}
              </Button>
              <DateField
                label={t("booking.labels.startDate")}
                value={startDate.toString()}
                onChange={(value) => {
                  setStartDate(new Date(value));
                }}
              />
              <Button
                variant="contained"
                onClick={() => {
                  setStartDate(
                    new Date(
                      startDate.setDate(startDate.getDate() + displayDays),
                    ),
                  );
                }}
                endIcon={<FiChevronsRight size={16} />}
              >
                {t("kmos.labels.nextDays", { days: displayDays })}
              </Button>
            </Grid>
            <Grid
              id="kmos-header-buttons"
              item
              xs={1}
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 1,
              }}
            >
              {isSegmentsReleased && (
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => {
                    setShowTimeSlotEditor(true);
                  }}
                  startIcon={<FiClock size={16} />}
                >
                  {t("kmos.labels.timeSlots")}
                </Button>
              )}
              {isKeyMasterRoleReleased && (
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => {
                    setShowSettings(true);
                  }}
                  startIcon={<FiSettings size={16} />}
                >
                  {t("menuItems.settings")}
                </Button>
              )}
              {canUpdateKmosRules &&
                isLaneAllocationRulesReleased && (
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => {
                      setShowAllocationRules(true);
                    }}
                    startIcon={<FiGitBranch size={16} />}
                  >
                    {t("kmos.labels.rules")}
                  </Button>
                )}
            </Grid>
          </>
        )}
      </Grid>
      <LaneAllocationView
        show={showAllocationRules}
        onClose={() => {
          setShowAllocationRules(false);
        }}
      />
      <KmosTimeSlotEditor
        show={showTimeSlotEditor}
        onUpdate={updateTimeSlotEditor}
        readonly={isMoveTimeSlotManagerReleased}
        onClose={() => setShowTimeSlotEditor(false)}
      />
      <KmosSettingsDialog
        open={showSettings}
        onClose={() => setShowSettings(false)}
        preferences={session.current.site?.preferences || ({} as IPreferences)}
        onSave={savePrefs}
      />
      {isKeyMasterTimeSlotReleased ? (
        <>
          <Tabs
            sx={{ mb: 2 }}
            value={activeTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            centered
          >
            <Tab label={t("kmos.labels.laneAllocator") || "Lane Allocator"} />
            <Tab
              label={t("kmos.labels.lanesByTimeSlot") || "Lanes By Time Slot"}
            />
            {shouldRenderPairedJourneysTab && (
              <Tab label={t("pair.title") || "Paired Journeys"} />
            )}
          </Tabs>
          {activeTab === 0 && (
            <Table>
              <KmosDateHeaderRow
                dates={dateRange}
                subHeader={t("kmos.labels.dueInSpaces")}
              />
              {uniqueTimeSlots.map((uts, index) => (
                <KmosTimeSlotRow
                  openDialog={() => setIsDialogOpen(true)}
                  setSelectedTsv={(tsv: IKmosV2AllocationView) =>
                    setSelectedTsv(tsv)
                  }
                  key={`${uts.startTime}-${index}`}
                  timeRange={`[${uts.startTime} - ${uts.endTime}]`}
                  title={uts.title}
                  timeSlot={uts.timeSlot}
                  dates={dateRange}
                  tsViews={getViewsByTimeSlot(uts)}
                />
              ))}
            </Table>
          )}
          {activeTab === 1 && (
            <LanesByTimeSlotContent
              startDate={startDate}
              setStartDate={setStartDate}
              daysToDisplay={daysToDisplay}
            />
          )}
          {activeTab === 2 && shouldRenderPairedJourneysTab && (
            <PairedJourneys
              data={pairedJourneysData}
              status={pairedJourneysStatus}
              getData={getPairedJourneysData}
            />
          )}
        </>
      ) : (
        <Table>
          <KmosDateHeaderRow
            dates={dateRange}
            subHeader={t("kmos.labels.dueInSpaces")}
          />
          {uniqueTimeSlots.map((uts, index) => (
            <KmosTimeSlotRow
              openDialog={() => setIsDialogOpen(true)}
              setSelectedTsv={(tsv: IKmosV2AllocationView) =>
                setSelectedTsv(tsv)
              }
              key={`${uts.startTime}-${index}`}
              timeRange={`[${uts.startTime} - ${uts.endTime}]`}
              title={uts.title}
              timeSlot={uts.timeSlot}
              dates={dateRange}
              tsViews={getViewsByTimeSlot(uts)}
            />
          ))}
        </Table>
      )}
    </>
  );
};

export default KmosView2;

/**
 * From: https://holidayextras.jira.com/browse/PKI-5900
 * A light green indicates that either there is no lane assigned, but no vehicles due for that time segment, or that the number of vehicles due is equal to the number of spaces.  In this case, if all vehicles turn up, the lane will be full.
 * Light green: 178, 232, 192
 *
 * A dark green indicates there are spaces available in the assigned lane.  No action necessary.
 * Dark green: 43, 189, 82
 *
 * A red line indicates that there is no lane allocated and there are bookings due that will return on the given date and time slot, a lane should be assigned.
 * Pink: 255, 192, 203
 *
 * An orange line indicates that a lane has been allocated, but there are insufficient spaces available to meet the number of arrivals, another lane should be assigned.
 * Orange: 255, 185, 115
 */
export const getTimeSlotDateStateColours = (
  spaces: number,
  due: number,
  hasAllocations: boolean,
) => {
  if (due === spaces) {
    return ["rgb(178, 232, 192)", "black", styles.allocationsEqual];
  }

  if (due < spaces) {
    return ["rgb(43, 189, 82)", "black", styles.allocationsGreater];
  }

  if (hasAllocations) {
    return ["rgb(255, 185, 115)", "black", styles.allocationsShort];
  }

  return ["rgb(255, 192, 203)", "black", styles.allocationsRequired];
};

// Get tab from URL or default to 0
const getTabFromUrl = (): number => {
  const params = new URLSearchParams(location.search);
  const tab = params.get("tab");
  return tab ? parseInt(tab, 10) : 0;
};
